import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LoyaltyBenefitIcon from 'loyalty/components/LoyaltyBenefitIcon'
import { formatBenefitLabel } from 'loyalty/utils/loyaltyBenefitLabels'
import LoyaltyBenefitIconContainer from 'LuxKit/loyalty/LoyaltyBenefitIconContainer'
import Colour from 'components/utils/Colour'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import useSocieteCampaign from 'loyalty/hooks/useSocieteCampaign'
import { useSocieteInterpolate } from 'loyalty/hooks/useSocieteInterpolate'
import { LEBT_SOCIETE_CAMPAIGN_SLUG, LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useState } from 'react'
import { LoyaltyTierCard, LoyaltyTierCardHeader, LoyaltyTierCardBody } from 'LuxKit/loyalty/LoyaltyTierCard'
import useSocieteCampaignTargetTier from 'loyalty/hooks/useSocieteCampaignTargetTier'

const MAX_BENEFITS_COLLAPSED = 3

interface Props {
  onDismiss: () => void
}

function SocieteCampaignUnlockTierCard({ onDismiss }: Props) {
  const givenName = useAppSelector(state => state.auth.account.givenName)
  const campaign = useSocieteCampaign(LEBT_SOCIETE_CAMPAIGN_SLUG)
  const { targetTier, targetTierDisplayName } = useSocieteCampaignTargetTier()
  const interpolate = useSocieteInterpolate()
  const [loyaltyConfig] = useLoyaltyConfig()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!campaign || !loyaltyConfig) return null

  const targetTierBenefits = loyaltyConfig.regionTierBenefits[targetTier]
  const visibleBenefits = isExpanded ? targetTierBenefits : targetTierBenefits.slice(0, MAX_BENEFITS_COLLAPSED)
  const hasMore = targetTierBenefits.length > MAX_BENEFITS_COLLAPSED && !isExpanded

  const headingPrefix = givenName ? `${givenName}, unlock ` : 'Unlock '

  return <LoyaltyTierCard>
    <LoyaltyTierCardHeader tier={targetTier} row>
      <Heading variant="heading4">
        {headingPrefix}
        <Colour value={`tier-${targetTier}-light-contrast`}>Société {targetTierDisplayName}</Colour>
      </Heading>
      <IconButton kind="tertiary" variant="ghost" size="small" shape="square" onClick={onDismiss} aria-label="Dismiss" style={{ '--button-color': 'currentColor', '--button-hover-color': 'currentColor' }}>
        <LineTimesIcon />
      </IconButton>
    </LoyaltyTierCardHeader>
    <LoyaltyTierCardBody padding={`${rem(20)} ${rem(20)} ${rem(24)}`} gap={16}>
      <BodyText variant="large">
        {interpolate(campaign.hero.subtitle, {
          qualificationEndDate: (vars) => <BodyText as="span" variant="large" weight="bold">{vars.qualificationEndDate}</BodyText>,
        })}
      </BodyText>
      <VerticalSpacer gap={8}>
        <BodyText variant="medium" weight="bold">{targetTierDisplayName} benefits</BodyText>
        <div style={{ display: 'flex', flexDirection: 'column', gap: rem(4) }}>
          {visibleBenefits.map(benefit => {
            const label = formatBenefitLabel(benefit)
            if (!label) return null
            return <div key={benefit.code} style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
              <LoyaltyBenefitIconContainer className={`tier-${targetTier}`}>
                <LoyaltyBenefitIcon benefitType={benefit.code} />
              </LoyaltyBenefitIconContainer>
              <BodyText variant="medium" colour="neutral-two">{label}</BodyText>
            </div>
          })}
        </div>
        {hasMore && <TextLink size="medium" weight="bold" onClick={() => setIsExpanded(true)}>
          + more
        </TextLink>}
      </VerticalSpacer>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TextButton kind="secondary" size="medium" to={`/${LOYALTY_LANDING_PAGE}`} onClick={onDismiss}>
          Learn more
        </TextButton>
      </div>
    </LoyaltyTierCardBody>
  </LoyaltyTierCard>
}

export default SocieteCampaignUnlockTierCard
